import { useState, useEffect, useMemo } from 'react';
import { Loader2, Search, Trash2, Ban, ChevronDown, ChevronUp, Users, DollarSign, BarChart2, PauseCircle, PlayCircle } from 'lucide-react';
import api from '@/api';

/** Reusable framed section with header, expand/collapse, and body. */
function Frame({ testId, Icon, title, subtitle, badge, expanded, onToggle, children }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border-2 border-gray-200 bg-white overflow-hidden"
    >
      <button
        data-testid={`${testId}-toggle`}
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-gray-900 text-white">
            <Icon size={15} />
          </div>
          <div className="text-left">
            <p className="text-sm font-extrabold text-gray-900 leading-tight">{title}</p>
            {subtitle && <p className="text-[10px] text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {badge && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-900 text-white">
              {badge}
            </span>
          )}
          {expanded
            ? <ChevronUp size={16} className="text-gray-400" />
            : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </button>
      <div className="border-t border-gray-100 px-4 py-3">
        {children}
      </div>
    </div>
  );
}

/** Users frame — shows recent 5 by default, expand to full list. */
function UsersFrame({
  users, filteredUsers, nonAdminFiltered, userSearch, setUserSearch,
  regPaused, handleToggleRegistration, selectedUsers, setSelectedUsers,
  toggleSelectAll, allSelected, toggleSelectUser, handleMassDelete,
  handleUpdateUserStatus, handleDeleteUser, cleaningSpam, loading,
}) {
  const [expanded, setExpanded] = useState(false);

  // Per-column filters
  const [countryFilter, setCountryFilter] = useState('all');
  const [subscribedFilter, setSubscribedFilter] = useState('all');  // all | Y | N
  const [statusFilter, setStatusFilter] = useState('all');           // all | active | suspended | banned | deleted
  const [lastLoginSort, setLastLoginSort] = useState('recent');      // recent | oldest | never

  const getCountry = (u) => (u.preferences?.country || u.country || '').trim();

  // Country options — unique non-empty countries sorted alphabetically
  const countryOptions = useMemo(() => {
    const set = new Set();
    filteredUsers.forEach(u => {
      const c = getCountry(u);
      if (c) set.add(c);
    });
    return ['all', ...Array.from(set).sort()];
  }, [filteredUsers]);

  // Apply column filters on top of the parent's text search
  const columnFiltered = useMemo(() => {
    let list = filteredUsers.filter(u => {
      if (countryFilter !== 'all' && getCountry(u) !== countryFilter) return false;
      const subY = u.subscription_status === 'active';
      if (subscribedFilter === 'Y' && !subY) return false;
      if (subscribedFilter === 'N' && subY) return false;
      if (statusFilter !== 'all' && (u.status || 'active') !== statusFilter) return false;
      if (lastLoginSort === 'never' && u.last_login_at) return false;
      return true;
    });
    if (lastLoginSort === 'recent') {
      list = [...list].sort((a, b) => (b.last_login_at || '').localeCompare(a.last_login_at || ''));
    } else if (lastLoginSort === 'oldest') {
      list = [...list].sort((a, b) => (a.last_login_at || '9999').localeCompare(b.last_login_at || '9999'));
    }
    return list;
  }, [filteredUsers, countryFilter, subscribedFilter, statusFilter, lastLoginSort]);

  const visible = expanded ? columnFiltered : columnFiltered.slice(0, 5);
  const activeFilterCount = (countryFilter !== 'all' ? 1 : 0)
    + (subscribedFilter !== 'all' ? 1 : 0)
    + (statusFilter !== 'all' ? 1 : 0)
    + (lastLoginSort !== 'recent' ? 1 : 0);

  const formatLastLogin = (iso) => {
    if (!iso) return 'Never';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '—';
      const diffHrs = (Date.now() - d.getTime()) / 3_600_000;
      if (diffHrs < 1) return `${Math.max(1, Math.round(diffHrs * 60))}m ago`;
      if (diffHrs < 24) return `${Math.round(diffHrs)}h ago`;
      if (diffHrs < 24 * 30) return `${Math.round(diffHrs / 24)}d ago`;
      return d.toLocaleDateString();
    } catch { return '—'; }
  };

  return (
    <Frame
      testId="users-frame"
      Icon={Users}
      title="Users"
      subtitle={`${users.length} total · showing ${visible.length}`}
      badge={expanded ? 'Show less' : 'Show all'}
      expanded={expanded}
      onToggle={() => setExpanded(e => !e)}
    >
      {/* Pause signups toggle — ALWAYS visible so admin can act without expanding */}
      <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          {regPaused ? (
            <div
              data-testid="admin-registration-paused-banner"
              className="px-3 py-2 rounded-xl border-2 border-amber-400 bg-amber-50 text-amber-800 text-[11px] font-semibold"
            >
              ⚠ New user registrations are PAUSED. New sign-ups see:
              <em className="font-normal block mt-0.5">“We are currently undergoing maintenance. Kindly try later.”</em>
            </div>
          ) : (
            <p className="text-[11px] text-gray-500">New users can register normally.</p>
          )}
        </div>
        <button
          data-testid="admin-toggle-registration"
          onClick={handleToggleRegistration}
          className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-xs font-bold transition-all whitespace-nowrap ${regPaused ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}`}
        >
          {regPaused ? <><PlayCircle size={14} /> Resume Signups</> : <><PauseCircle size={14} /> Pause new users</>}
        </button>
      </div>

      {/* Search + filters — ALWAYS visible (not hidden behind expand) */}
      <div className="mb-3 space-y-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="admin-user-search"
            value={userSearch}
            onChange={e => { setUserSearch(e.target.value); setSelectedUsers(new Set()); }}
            placeholder="Search users by name or email (use * for wildcard, e.g. L*)"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white border border-gray-200 text-sm outline-none focus:border-gray-400"
          />
        </div>
        <div className="flex flex-wrap gap-2 items-center text-[11px]">
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mr-1">Filter:</span>
          <label className="flex items-center gap-1.5">
            <span className="text-gray-500">Country</span>
            <select
              data-testid="admin-filter-country"
              value={countryFilter}
              onChange={e => setCountryFilter(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-[11px] font-semibold outline-none focus:border-gray-400"
            >
              {countryOptions.map(c => (
                <option key={c} value={c}>{c === 'all' ? 'All' : c}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-gray-500">Subscribed</span>
            <select
              data-testid="admin-filter-subscribed"
              value={subscribedFilter}
              onChange={e => setSubscribedFilter(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-[11px] font-semibold outline-none focus:border-gray-400"
            >
              <option value="all">All</option>
              <option value="Y">Y</option>
              <option value="N">N</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-gray-500">Last login</span>
            <select
              data-testid="admin-filter-last-login"
              value={lastLoginSort}
              onChange={e => setLastLoginSort(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-[11px] font-semibold outline-none focus:border-gray-400"
            >
              <option value="recent">Recent first</option>
              <option value="oldest">Oldest first</option>
              <option value="never">Never logged in</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-gray-500">Status</span>
            <select
              data-testid="admin-filter-status"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-[11px] font-semibold outline-none focus:border-gray-400"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="banned">Banned</option>
              <option value="deleted">Deleted</option>
            </select>
          </label>
          {activeFilterCount > 0 && (
            <button
              data-testid="admin-filter-clear"
              onClick={() => { setCountryFilter('all'); setSubscribedFilter('all'); setStatusFilter('all'); setLastLoginSort('recent'); }}
              className="ml-auto text-[10px] text-blue-600 hover:text-blue-700 font-semibold uppercase tracking-wider"
            >
              Clear ({activeFilterCount})
            </button>
          )}
          <span className={activeFilterCount > 0 ? '' : 'ml-auto'}>
            <span className="text-[10px] text-gray-400">{columnFiltered.length} match{columnFiltered.length === 1 ? '' : 'es'}</span>
          </span>
        </div>
      </div>

      {expanded && nonAdminFiltered.length > 0 && (
        <div className="flex items-center gap-3 mb-3 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200">
          <input type="checkbox" data-testid="admin-select-all" checked={allSelected} onChange={toggleSelectAll}
            className="w-4 h-4 rounded border-gray-300 accent-red-600 cursor-pointer" />
          <span className="text-xs text-gray-600">
            {selectedUsers.size > 0 ? `${selectedUsers.size} of ${nonAdminFiltered.length} selected` : `Select all ${nonAdminFiltered.length} results`}
          </span>
          {selectedUsers.size > 0 && (
            <button data-testid="admin-mass-delete" onClick={handleMassDelete} disabled={cleaningSpam}
              className="ml-auto px-4 py-1.5 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-700 disabled:opacity-60 transition-all flex items-center gap-1.5">
              {cleaningSpam ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
              Delete {selectedUsers.size}
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-6"><Loader2 size={22} className="animate-spin mx-auto text-gray-400" /></div>
      ) : (
        <div className="space-y-2">
          {visible.map(u => {
            const subscribed = u.subscription_status === 'active';
            const country = u.preferences?.country || u.country || '—';
            const lastLogin = formatLastLogin(u.last_login_at);
            const isAdmin = u.role === 'admin';
            const isDeleted = u.status === 'deleted';
            const isSuspended = u.status === 'suspended' || u.status === 'banned';
            return (
              <div key={u.id} className={`bg-white rounded-xl border p-3 ${selectedUsers.has(u.id) ? 'border-red-300 bg-red-50/30' : 'border-gray-100'}`}>
                <div className="flex items-center gap-3">
                  {expanded && !isAdmin && (
                    <input type="checkbox" checked={selectedUsers.has(u.id)} onChange={() => toggleSelectUser(u.id)}
                      className="w-4 h-4 rounded border-gray-300 accent-red-600 cursor-pointer shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">{u.name}</p>
                    <p className="text-xs text-gray-400 truncate">{u.email || u.mobile}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[10px] text-gray-500">
                      <span data-testid={`user-country-${u.id}`}>
                        <span className="text-gray-400 uppercase tracking-wider text-[9px] mr-1">Country</span>
                        <span className="font-semibold text-gray-700">{country}</span>
                      </span>
                      <span data-testid={`user-subscribed-${u.id}`}>
                        <span className="text-gray-400 uppercase tracking-wider text-[9px] mr-1">Subscribed</span>
                        <span className={`font-bold ${subscribed ? 'text-green-600' : 'text-gray-500'}`}>{subscribed ? 'Y' : 'N'}</span>
                      </span>
                      <span data-testid={`user-last-login-${u.id}`}>
                        <span className="text-gray-400 uppercase tracking-wider text-[9px] mr-1">Last login</span>
                        <span className="font-semibold text-gray-700">{lastLogin}</span>
                      </span>
                    </div>
                    <div className="flex gap-2 mt-1.5">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${u.status === 'active' ? 'bg-green-50 text-green-600' : u.status === 'suspended' ? 'bg-yellow-50 text-yellow-600' : u.status === 'deleted' ? 'bg-gray-100 text-gray-500' : 'bg-red-50 text-red-600'}`}>{u.status || 'active'}</span>
                      {isAdmin && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase bg-purple-50 text-purple-600">admin</span>}
                    </div>
                  </div>
                  {/* Per-user action buttons — ALWAYS visible (not hidden behind expand) */}
                  {!isAdmin && !isDeleted && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        data-testid={`admin-suspend-user-${u.id}`}
                        onClick={() => handleUpdateUserStatus(u.id, isSuspended ? 'active' : 'suspended')}
                        title={isSuspended ? 'Reactivate account' : 'Suspend account (sends email)'}
                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors ${isSuspended ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'}`}
                      >
                        <Ban size={12} /> {isSuspended ? 'Unsuspend' : 'Suspend'}
                      </button>
                      <button
                        data-testid={`admin-delete-user-${u.id}`}
                        onClick={() => handleDeleteUser(u.id, u.name)}
                        title="Delete account (soft-delete; sends email; retains transactions)"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-[10px] font-bold uppercase tracking-wider transition-colors"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  )}
                  {isDeleted && (
                    <span className="text-[10px] font-semibold text-gray-400 italic shrink-0">deleted</span>
                  )}
                </div>
              </div>
            );
          })}
          {visible.length === 0 && <p className="text-center text-sm text-gray-400 py-6">No users match your search</p>}
        </div>
      )}
    </Frame>
  );
}

/** Revenue frame — total amounts + recent transactions (collapsed summary, expand for full table). */
function RevenueFrame() {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await api.adminGetUsersRevenue();
        if (!cancel) setData(res);
      } catch (_e) {
        if (!cancel) setData(null);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const byCurrency = data?.by_currency || {};
  const currencies = Object.keys(byCurrency);
  const totalTxns = data?.total_paid_transactions || 0;
  const recent = data?.recent_transactions || [];
  const visibleTxns = expanded ? recent : recent.slice(0, 5);

  const fmtMoney = (amount, cur) => {
    const symbol = cur === 'gbp' ? '£' : cur === 'usd' ? '$' : cur === 'inr' ? '₹' : cur === 'eur' ? '€' : '';
    return `${symbol}${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <Frame
      testId="revenue-frame"
      Icon={DollarSign}
      title="User Revenue"
      subtitle={`${totalTxns} paid transactions · ${data?.paid_last_30d || 0} in last 30d`}
      badge={expanded ? 'Show less' : 'Show all'}
      expanded={expanded}
      onToggle={() => setExpanded(e => !e)}
    >
      {loading ? (
        <div className="text-center py-6"><Loader2 size={22} className="animate-spin mx-auto text-gray-400" /></div>
      ) : currencies.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">No paid transactions yet.</p>
      ) : (
        <>
          {/* Totals grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            {currencies.map(cur => (
              <div
                key={cur}
                data-testid={`revenue-total-${cur}`}
                className="rounded-xl p-3 bg-green-50 border border-green-100"
              >
                <p className="text-[9px] font-bold tracking-widest uppercase text-green-700/70">Total · {cur}</p>
                <p className="text-lg font-black text-green-700 mt-0.5">
                  {fmtMoney(byCurrency[cur].total_amount, cur)}
                </p>
                <p className="text-[10px] text-green-600/80 mt-0.5">{byCurrency[cur].count} txns</p>
              </div>
            ))}
          </div>

          {/* Recent transactions table */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-1">
              {expanded ? 'All payment records' : 'Recent 5'}
            </p>
            {visibleTxns.map((t, i) => (
              <div
                key={t.id || i}
                data-testid={`revenue-txn-${i}`}
                className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-xs"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 truncate">{t.user_id}</p>
                  <p className="text-[10px] text-gray-500 truncate">
                    {t.paid_at ? new Date(t.paid_at).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </p>
                </div>
                <div className="text-right shrink-0 ml-2">
                  <p className="font-black text-green-700">{fmtMoney(t.amount, t.currency)}</p>
                  <p className="text-[9px] text-gray-500 uppercase">{t.metadata?.type || t.status || 'payment'}</p>
                </div>
              </div>
            ))}
            {visibleTxns.length === 0 && (
              <p className="text-center text-xs text-gray-400 py-3">No transactions.</p>
            )}
          </div>
        </>
      )}
    </Frame>
  );
}

/** Behaviour frame — top 10 visited categories from page_visits. */
function BehaviourFrame() {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await api.adminGetUsersBehaviour();
        if (!cancel) setData(res);
      } catch (_e) {
        if (!cancel) setData(null);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const topCats = data?.top_categories || [];
  const totalVisits = data?.total_visits || 0;
  const visibleCats = expanded ? topCats : topCats.slice(0, 5);
  const maxVisits = Math.max(1, ...topCats.map(c => c.visits));

  return (
    <Frame
      testId="behaviour-frame"
      Icon={BarChart2}
      title="User Behaviour"
      subtitle={`${totalVisits.toLocaleString()} visits · ${data?.unique_users || 0} users · last ${data?.window_days || 30}d`}
      badge={expanded ? 'Show less' : 'Show all'}
      expanded={expanded}
      onToggle={() => setExpanded(e => !e)}
    >
      {loading ? (
        <div className="text-center py-6"><Loader2 size={22} className="animate-spin mx-auto text-gray-400" /></div>
      ) : topCats.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">
          No visits tracked yet. The Dashboard pill-tracker starts filling this in as users browse.
        </p>
      ) : (
        <div className="space-y-1.5">
          {visibleCats.map((c, i) => (
            <div
              key={`${c.tab}-${c.category}-${i}`}
              data-testid={`behaviour-row-${i}`}
              className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2"
            >
              <div className="flex items-center justify-between text-xs">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 truncate">
                    <span className="text-gray-400 text-[10px] uppercase tracking-widest mr-1.5">{c.tab}</span>
                    {c.category}
                  </p>
                </div>
                <div className="text-right shrink-0 ml-2">
                  <p className="font-black text-gray-900">{c.visits.toLocaleString()}</p>
                  <p className="text-[9px] text-gray-500">{c.unique_users} users</p>
                </div>
              </div>
              <div className="mt-1.5 h-1 rounded-full bg-gray-200 overflow-hidden">
                <div
                  className="h-full bg-gray-900"
                  style={{ width: `${(c.visits / maxVisits) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </Frame>
  );
}

export default function UsersTab(props) {
  return (
    <div className="mt-2 space-y-4" data-testid="users-tab">
      <UsersFrame {...props} />
      <RevenueFrame />
      <BehaviourFrame />
    </div>
  );
}
